import { forwardRef, useEffect, useMemo, useState } from 'react';
import className from 'classnames/bind';
import { motion, AnimatePresence } from 'motion/react';
import { useSoundContext } from '@/providers/SoundContext';
import { useClientMount } from '@/providers/ClientMountProvider';
import { getHighlightMarkup } from '@/core/utils';
import { SoundIcon } from "@components";
import { easeOutQuad, easeOutBack, easeInBack } from '@/core/easing';
import SoundIconLarge from '@/assets/svg/sound-icon.svg';
import SoundPopupAccent from '@/assets/svg/sound-popup-accent.svg';
import CloseIcon from '@/assets/svg/close.svg';
import { usePageWrapperData } from '@/providers/PageWrapperContext';

import styles from './SoundPopup.module.scss';
const cx = className.bind(styles);

// Animation constants
const ANIM_SCALE = 0.75; // scale down amount
const ANIM_EASE_IN = easeInBack; // scaling down
const ANIM_EASE_OUT = easeOutBack; // scaling up
const ANIM_EASE_FADE = easeOutQuad; // fade

type SoundPopupProps = {
  hideLarge?: boolean;
  headlineOverride?: string;
};

const PopupSmall = forwardRef<HTMLButtonElement, SoundPopupProps>((_props, ref) => {
  const { hasInteracted, soundEnabled, setSoundEnabled } = useSoundContext();
  const { labels: { soundPrompt: { soundOffTooltip, disableSound, enableSound } = {} } = {} }  = usePageWrapperData();
  const [isWaiting, setIsWaiting] = useState<boolean>(true);

  const onClick = () => setSoundEnabled(!soundEnabled);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (hasInteracted && isWaiting) {
      setTimeout(() => {
        setIsWaiting(false);
      }, 1000);
    }
  }, [hasInteracted]);

  return (
    <button
      className={cx(["popup-small"])}
      ref={ref}
      onClick={onClick}
      aria-label={soundEnabled ? disableSound : enableSound}
    >
      <p className={cx(["tooltip", "eyebrow-s", { hidden: soundEnabled, waiting: isWaiting }])}>{soundOffTooltip}</p>
      <SoundIcon animating={soundEnabled} />
    </button>
  )
});

const PopupLarge = forwardRef<HTMLDivElement, SoundPopupProps>(({ headlineOverride }, ref) => {
  const { labels } = usePageWrapperData()
  const { soundPrompt: { headline, soundOn, soundOff } = {} } = labels;
  const { setSoundEnabled, setHasInteracted } = useSoundContext();
  const headlineMarkup = getHighlightMarkup(headlineOverride || headline);

  const onClose = () => {
    // Closing the popup counts as a denial
    setSoundEnabled(false);
    setHasInteracted(true);
  }

  const onSoundToggle = (enabled: boolean) => {
    setSoundEnabled(enabled);
    setHasInteracted(true);
  }

  return (
    <div className={cx(["popup-large"])} ref={ref}>
      <div className={cx("copy")}>
        <p className={cx(["headline", "title-uc-xs"])} dangerouslySetInnerHTML={{ __html: headlineMarkup }} />
        <div className={cx("buttons")}>
          <button className={cx("sound-on")} onClick={() => onSoundToggle(true)} aria-label={soundOn}>
            <SoundIconLarge />
            <p className="cta">{soundOn}</p>
          </button>
          <button className={cx("sound-off")} onClick={() => onSoundToggle(false)} aria-label={soundOff}>
            {soundOff}
          </button>
        </div>
      </div>
      <div className={cx("icon-container")}>
        <div className={cx("icon-wrapper")}>
          <SoundPopupAccent className={cx("accent")} />
          <SoundIcon className={cx("bars")} animating={true} />
        </div>
      </div>
      <button className={cx("close-button")} onClick={onClose} aria-label="Close sound options">
        <CloseIcon className={cx("icon")} />
      </button>
    </div>
  )
});

const AnimatedPopupSmall = motion.create(PopupSmall);
const AnimatedPopupLarge = motion.create(PopupLarge);

const SoundPopup = forwardRef<HTMLDivElement, SoundPopupProps>((props, ref) => {
  const { hasInteracted } = useSoundContext();
  const { mounted } = useClientMount();

  // Don't render anything until mounted to prevent flash with wrong styles
  if (!mounted) return null;

  return (
    <div className={cx(["sound-popup"])} ref={ref}>
      <AnimatePresence mode="wait">
        {
          (!hasInteracted && !props.hideLarge) ? (
            <AnimatedPopupLarge
              key="large"
              {...props}
              initial={{ opacity: 0, scale: ANIM_SCALE }}
              animate={{ opacity: 1, scale: 1, transition: { delay: 1, duration: 0.35, ease: ANIM_EASE_OUT } }}
              exit={{ opacity: 0, scale: ANIM_SCALE, transition: { duration: 0.35, ease: ANIM_EASE_IN } }}
            />
          ) : (
            <AnimatedPopupSmall
              key="small"
              {...props}
              initial={!props.hideLarge ? { opacity: 0 } : { opacity: 1 }}
              animate={{ opacity: 1, transition: { duration: 0.35, ease: ANIM_EASE_FADE } }}
            />
          )
        }
      </AnimatePresence>
    </div>
  )
});

export default motion.create(SoundPopup);
